import { useState } from 'react';
import { useLeadStore } from '@/store/useLeadStore';
import { submitLead } from '@/services/api';
import { WidgetConfig } from '@/types';

/**
 * Custom hook for handling lead submission via the BFF.
 * 
 * This hook handles the lead submission process by:
 * 1. Validating form data
 * 2. Calling the BFF API endpoint (which handles backend authentication)
 * 3. Managing loading and error states
 * 4. Notifying parent window on success (for widget mode)
 * 
 * @param config - Widget configuration (clientName, etc.)
 * @returns Object with submit function, loading state, and error state
 */
export const useLeadSubmit = (config: WidgetConfig) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { nextStep } = useLeadStore();

  const submit = async () => {
    // Get the latest formData from the store at submission time
    const { formData } = useLeadStore.getState();
    
    // For commerce, check surfaceRdc; for others, check surface
    const isCommerce = formData.propertyType === 'commerce';
    const hasSurface = isCommerce 
      ? formData.surfaceRdc !== null && formData.surfaceRdc !== undefined
      : formData.surface !== null && formData.surface !== undefined;
    
    // Validate required fields
    if (!formData.estimationType || !formData.propertyType || !formData.address || !hasSurface || !formData.user) {
      setError('Données du formulaire incomplètes');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Map frontend types to backend types
      const propertyTypeMap: Record<string, string> = {
        'prix': 'Sale',
        'loyer': 'Rental',
      };
      
      const assetClassMap: Record<string, string> = {
        'bureaux': 'Office',
        'commerce': 'Commercial',
        'local-activite': 'Business_premises',
        'industriel': 'Logistics',
      };

      // Calculate total surface for commerce (RDC + sous-sol)
      const isCommerce = formData.propertyType === 'commerce';
      const totalSurface = isCommerce
        ? (formData.surfaceRdc || 0) + (formData.surfaceSousSol || 0)
        : formData.surface || 0;

      // Prepare payload for the backend (via BFF)
      const payload: any = {
        address: formData.address,
        surface: totalSurface,
        contact: {
          nom: formData.user.lastName,
          prenom: formData.user.firstName,
          email: formData.user.email,
          entreprise: formData.user.company || '',
          ...(formData.user.phone && { phone: formData.user.phone }),
        },
        property_type: propertyTypeMap[formData.estimationType] || 'Sale',
        asset_class: assetClassMap[formData.propertyType] || 'Commercial',
        client: config.clientName,
        surface_variation: 20,  // Default variation percentage
      };

      // Add basement_surface for commerce (Commercial asset class)
      if (isCommerce && formData.surfaceSousSol && formData.surfaceSousSol > 0) {
        payload.basement_surface = formData.surfaceSousSol;
      }

      // Submit to BFF (no authentication needed from client side)
      await submitLead(payload);

      // Notify parent window if in widget mode
      if (window.parent !== window) {
        window.parent.postMessage(
          {
            type: 'leadSubmitted',
            data: payload,
          },
          '*'
        );
      }

      nextStep();
    } catch (err) {
      setError('Une erreur est survenue, veuillez réessayer dans quelques minutes.');
      console.error('Lead submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  return {
    submit,
    loading,
    error,
  };
};
